import { Typography } from '@neo/primitives/typography'
import { Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import type { RExternalAppDetails } from 'v2/types/api/settings'

// Decorative: the whole row is the button, so this must never be a nested `<button>`.
export const AddAffordance = () => (
  <span
    className="flex h-8 w-8 flex-none items-center justify-center rounded-full border border-st1 text-secondary transition-colors group-hover:border-secondary group-hover:text-foreground"
    aria-hidden="true"
  >
    <Plus className="h-4 w-4" />
  </span>
)

interface CatalogRowProps {
  icon: ReactNode
  title: string
  description: string
  onClick: () => void
  testId: string
}

// `px-3` is room for the hover background, not an indent to remove for alignment.
export const CatalogRow = ({ icon, title, description, onClick, testId }: CatalogRowProps) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-hv1"
  >
    {icon}
    <span className="flex min-w-0 flex-1 flex-col gap-1">
      <Typography as="span" variant="P/500" className="truncate text-secondary">
        {title}
      </Typography>
      <Typography as="span" variant="XS/400" className="truncate text-tertiary">
        {description}
      </Typography>
    </span>
    <AddAffordance />
  </button>
)

// Several catalog logo URLs 404; the initial is shown instead of a broken image.
type LogoApp = Pick<RExternalAppDetails, 'id' | 'name' | 'logo_url'>

export const AppLogoMark = ({
  app,
  testIdPrefix = 'logo-fallback',
}: {
  app: LogoApp
  testIdPrefix?: string
}) => {
  const [failed, setFailed] = useState(false)

  if (failed || !app.logo_url)
    return (
      <span
        className="flex h-full w-full items-center justify-center rounded-md bg-st2 text-p-600 text-secondary"
        aria-hidden="true"
        data-testid={`${testIdPrefix}-${app.id}`}
      >
        {app.name.trim().charAt(0).toUpperCase()}
      </span>
    )

  return (
    // `rounded` on the image too: some logos are PNGs with an opaque square background.
    <img
      src={app.logo_url}
      alt=""
      className="h-full w-full rounded object-contain"
      onError={() => setFailed(true)}
    />
  )
}

export const AppLogo = ({ app, testIdPrefix }: { app: LogoApp; testIdPrefix?: string }) => (
  // Separate from `AppLogoMark` so a caller with its own tile avoids a border inside a border.
  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-border bg-background p-1.5">
    <AppLogoMark app={app} testIdPrefix={testIdPrefix} />
  </span>
)
